import { Canvas, useLoader } from '@react-three/fiber'
import { Suspense, useMemo } from 'react'
import * as THREE from 'three'

import Lights from '@components/storeMap/floor/3d/Lights'
import SpinningUbicaCube from '@components/storeMap/floor/3d/SpinningUbicaCube'
import AxisLabels3D from '@components/storeMap/floor/3d/components/AxisLabels3D'
import { usePreloadImages } from '@hooks/usePreloadImages'
import { usePanoramaContext } from '@routes/Panorama/PanoramaImageContext'
import { useGraphNode } from '@routes/Panorama/data/panoramaNetwork'
import CameraControls from '@routes/Panorama/image/CameraControls'
import FovZoom from '@routes/Panorama/image/FovZoom'
import GroundMarkers from '@routes/Panorama/image/GroundMarkers'

import { calculateRelativeAngle } from '../utils'

import type { PanoramaImage } from '@state/panorama'

const DOME_RADIUS = 50

function usePreloadConnectedImages(currentPanoramaImage?: PanoramaImage) {
  const currentGraphNode = useGraphNode(currentPanoramaImage)

  const imageUrls = useMemo(() => {
    if (!currentGraphNode) return []
    return currentGraphNode.connectedPoints.map(p => p.url)
  }, [currentGraphNode])

  usePreloadImages(imageUrls)
}

function Image() {
  const { currentImage, previousImage, selectImage } = usePanoramaContext()

  const currentGraphNode = useGraphNode(currentImage)
  usePreloadConnectedImages(currentImage)

  const initialAzimuth = useMemo(() => {
    if (!currentGraphNode || !currentImage) return 0
    const connectedPoints = currentGraphNode.connectedPoints
    const target = connectedPoints.find(p => p.id !== previousImage?.id) ?? connectedPoints[0]!

    return calculateRelativeAngle(currentImage, target) + Math.PI
  }, [currentGraphNode, currentImage, previousImage?.id])

  if (!currentImage) return null

  return (
    <>
      <Canvas
        camera={{ position: [0, 0, 10], isPerspectiveCamera: true }}
        shadows
        frameloop="always"
        dpr={[1, 1.5]}
        className='rounded-2xl'
      >
        <Lights />

        <FovZoom panoramaImage={currentImage} />

        <Suspense fallback={<SpinningUbicaCube size={2} y={0} bobbing={true} />}>
          <CameraControls
            initialAzimuth={initialAzimuth}
            panoramaImage={currentImage}
          />
          <Dome panoramaImage={currentImage} />

          <GroundMarkers
            currentPanoramaImage={currentImage}
            onImageSelect={selectImage}
            domeRadius={DOME_RADIUS}
          />
        </Suspense>

        <AxisLabels3D />
      </Canvas>

    </>
  )
}

interface DomeProps {
  panoramaImage: PanoramaImage
}

function Dome({ panoramaImage }: DomeProps) {
  const texture = useLoader(THREE.TextureLoader, panoramaImage.url)

  // eslint-disable-next-line react-hooks/immutability
  texture.wrapS = THREE.RepeatWrapping
  // eslint-disable-next-line react-hooks/immutability
  texture.repeat.x = -1
  // eslint-disable-next-line react-hooks/immutability
  texture.needsUpdate = true

  return (
    <mesh>
      <sphereGeometry attach="geometry" args={[DOME_RADIUS, 60, 40]} />
      <meshBasicMaterial attach="material" map={texture} side={THREE.BackSide} />
    </mesh>
  )
}

export default Image